import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home, ChevronRight, Search, List, Download, MoreVertical, Plus,
  Gift, FileText, Calendar, CreditCard, AlertTriangle, Package, Trash2,
  Pencil, Receipt, SlidersHorizontal, ArrowDownUp, RefreshCw, X,
} from 'lucide-react';
import { V2_BASE } from '../config';
import { TIPO_CLIENTE, PAESI, fiscalFor } from '../../constants/anagrafica';
import AddCardModal from '../components/AddCardModal';
import './contabilita.css';

const api = axios.create({ withCredentials: true });

/* ── Helpers ─────────────────────────────────────────────── */
const EUR = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '0,00 €' : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);
};
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' - ' + dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

/* Mappa stati → tag */
const STATE_TAG = (raw) => {
  const s = (raw || '').toLowerCase();
  if (['pagata', 'pagato', 'completato', 'concluso', 'terminato', 'valido', 'attivo', 'paid'].includes(s))
    return { cls: 'ok', label: cap(raw) };
  if (['scaduta', 'scaduto', 'insoluto', 'annullata', 'annullato', 'rifiutato', 'failed'].includes(s))
    return { cls: 'danger', label: cap(raw) };
  if (['in_attesa', 'in attesa', 'pending', 'pending_payment', 'in_lavorazione'].includes(s))
    return { cls: 'warn', label: cap(raw) };
  return { cls: 'info', label: cap(raw) || '—' };
};
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, ' ') : '');

/* ── Componenti condivisi ────────────────────────────────── */

const Toggle = ({ on, onClick }) => (
  <button className={`nfx-toggle${on ? ' is-on' : ''}`} onClick={onClick} aria-pressed={on} type="button" />
);

const Crumb = ({ label }) => {
  const navigate = useNavigate();
  return (
    <div className="nfx-crumb">
      <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
      <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
      <span className="nfx-crumb__cur">{label}</span>
    </div>
  );
};

/* Wrapper pagina Contabilità: banner condizionale + breadcrumb + titolo */
const ContabPage = ({ crumb, title, intro, children }) => {
  const navigate = useNavigate();
  const [noMethod, setNoMethod] = useState(false);
  useEffect(() => {
    api.get('/api/metodi-pagamento')
      .then(r => setNoMethod(Array.isArray(r.data) ? r.data.length === 0 : true))
      .catch(() => setNoMethod(false));
  }, []);
  return (
    <div className="nfx-cb">
      {noMethod && (
        <div className="nfx-cb-banner">
          <span className="nfx-cb-banner__icon"><AlertTriangle size={22} /></span>
          <div className="nfx-cb-banner__text">
            <div className="nfx-cb-banner__title">Aggiungi una modalità di pagamento</div>
            <div className="nfx-cb-banner__desc">Aggiungere un metodo di pagamento per garantire il rinnovo automatico dei tuoi prodotti ed evitare interruzioni del servizio.</div>
          </div>
          <button className="nfx-cb-banner__btn" onClick={() => navigate(V2_BASE + '/billing/metodi-pagamento')}>
            Aggiungi una modalità di pagamento
          </button>
        </div>
      )}
      <Crumb label={crumb} />
      {title && <h1 className="nfx-cb-title">{title}</h1>}
      {intro}
      {children}
    </div>
  );
};

const Pager = ({ count }) => (
  <div className="nfx-pager">
    <div className="nfx-pager__left">
      <span>Risultati per pagina</span>
      <select defaultValue="25"><option>25</option><option>50</option><option>100</option></select>
    </div>
    <span>Pagina 1 di 1</span>
  </div>
);

const Empty = ({ icon, title, sub }) => (
  <div className="nfx-empty2">
    <span className="nfx-empty2__icon">{icon}</span>
    <div className="nfx-empty2__title">{title}</div>
    {sub && <div className="nfx-empty2__sub">{sub}</div>}
  </div>
);

const useFetch = (url, key, fallback) => {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const reload = React.useCallback(() => setNonce(n => n + 1), []);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(url)
      .then(r => { if (alive) setData(key ? (r.data?.[key] ?? fallback) : r.data); })
      .catch(() => { if (alive) setData(fallback); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [url, key, nonce]); // eslint-disable-line
  return [data, loading, reload];
};

/* ═══════════════════ 1. FATTURE ═══════════════════ */
export const FatturePage = () => {
  const [fatture, loading] = useFetch('/api/fatturazione/fatture', 'fatture', []);
  return (
    <ContabPage crumb="Fatture" title="Fatture">
      <div className="nfx-card">
        <div className="nfx-tb">
          <span className="nfx-tb__count"><b>{fatture.length}</b> {fatture.length === 1 ? 'Fattura' : 'Fatture'}</span>
          <button className="nfx-tb__btn">Pagare le fatture in sospeso</button>
          <span className="nfx-tb__spacer" />
          <div className="nfx-tb__actions">
            <button className="nfx-iconbtn"><MoreVertical size={18} /></button>
            <button className="nfx-iconbtn"><List size={18} /></button>
            <button className="nfx-iconbtn is-active"><Search size={18} /></button>
            <button className="nfx-iconbtn"><SlidersHorizontal size={18} /></button>
          </div>
        </div>
        <div className="nfx-filters">
          <div className="nfx-search-field"><Search size={16} /><input placeholder="Cercare un prodotto, un numero di fattura" /></div>
          <button className="nfx-addfilter"><Plus size={16} /> Aggiungere un filtro</button>
        </div>
        {loading ? <div className="nfx-loading">Caricamento…</div> : fatture.length === 0 ? (
          <Empty icon={<Receipt size={40} />} title="Nessuna fattura" />
        ) : (
          <table className="nfx-table">
            <thead><tr>
              <th className="nfx-th-check"><input type="checkbox" className="nfx-check" /></th>
              <th>N° della fattura</th><th>Data di creazione</th><th>Statuto</th>
              <th>Metodo di pagamento</th><th>Prodotti / Servizi</th><th className="num">Totale (TTC)</th><th className="nfx-td-actions" />
            </tr></thead>
            <tbody>
              {fatture.map((f, i) => {
                const st = STATE_TAG(f.stato);
                const free = parseFloat(f.totale || 0) === 0;
                return (
                  <tr key={f.id || i}>
                    <td className="nfx-td-check"><input type="checkbox" className="nfx-check" /></td>
                    <td><div className="nfx-cell-strong">{f.numero_fattura || '—'}</div>{f.tipo_documento && <div className="nfx-cell-sub">{cap(f.tipo_documento)}</div>}</td>
                    <td>{fmtDate(f.data_emissione)}</td>
                    <td><span className={`nfx-st nfx-st--${st.cls}`}>{st.label}</span></td>
                    <td>{st.cls === 'warn'
                      ? <button className="nfx-cfm-btn nfx-cfm-btn--default">Pagare la fattura</button>
                      : <span className="nfx-cell-inline">{free ? <Gift size={16} /> : <CreditCard size={16} />}{free ? 'Gratuito' : (f.metodo_pagamento || '—')}</span>}</td>
                    <td><span className="nfx-cell-inline"><Package size={16} /></span></td>
                    <td className="num nfx-cell-strong">{EUR(f.totale)}</td>
                    <td className="nfx-td-actions"><button className="nfx-iconbtn"><MoreVertical size={16} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pager />
      </div>
    </ContabPage>
  );
};

/* ═══════════════════ 2. TRANSAZIONI ═══════════════════ */
export const TransazioniPage = () => {
  const [pagamenti, loading] = useFetch('/api/fatturazione/pagamenti', 'pagamenti', []);
  return (
    <ContabPage crumb="Transazioni effettuate" title="Transazioni effettuate">
      <div className="nfx-card">
        <div className="nfx-tb">
          <span className="nfx-tb__count"><b>{pagamenti.length}</b> {pagamenti.length === 1 ? 'Transazione effettuata' : 'Transazioni effettuate'}</span>
          <span className="nfx-tb__spacer" />
          <div className="nfx-tb__actions">
            <button className="nfx-iconbtn"><Download size={18} /></button>
            <button className="nfx-iconbtn"><List size={18} /></button>
            <button className="nfx-iconbtn is-active"><Search size={18} /></button>
            <button className="nfx-iconbtn"><SlidersHorizontal size={18} /></button>
          </div>
        </div>
        <div className="nfx-filters">
          <div className="nfx-search-field"><Search size={16} /><input placeholder="Cercare un n° di transazione" /></div>
          <button className="nfx-addfilter"><Plus size={16} /> Aggiungere un filtro</button>
        </div>
        {loading ? <div className="nfx-loading">Caricamento…</div> : pagamenti.length === 0 ? (
          <Empty icon={<ArrowDownUp size={40} />} title="Nessuna transazione" />
        ) : (
          <table className="nfx-table">
            <thead><tr>
              <th className="nfx-th-check"><input type="checkbox" className="nfx-check" /></th>
              <th>N° del pagamento</th><th>Pagato il</th><th>Metodo di pagamento</th><th>Documenti</th><th className="num">Totale (TTC)</th><th className="nfx-td-actions" />
            </tr></thead>
            <tbody>
              {pagamenti.map((p, i) => {
                const free = parseFloat(p.importo || 0) === 0;
                return (
                  <tr key={p.id || i}>
                    <td className="nfx-td-check"><input type="checkbox" className="nfx-check" /></td>
                    <td className="nfx-cell-strong">{p.codice_pagamento || '—'}</td>
                    <td>{fmtDateTime(p.data_pagamento)}</td>
                    <td><span className="nfx-cell-inline">{free ? <Gift size={16} /> : <CreditCard size={16} />}{free ? 'Gratuito' : (p.metodo || '—')}</span></td>
                    <td><span className="nfx-cell-inline">{p.numero_fattura ? <><FileText size={16} /> 1 documento</> : '—'}</span></td>
                    <td className="num nfx-cell-strong">{EUR(p.importo)}</td>
                    <td className="nfx-td-actions"><button className="nfx-iconbtn"><MoreVertical size={16} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pager />
      </div>
    </ContabPage>
  );
};

/* ═══════════════════ 3. ORDINI ═══════════════════ */
export const OrdiniPage = () => {
  const [ordini, loading] = useFetch('/api/fatturazione/ordini', 'ordini', []);
  return (
    <ContabPage crumb="Ordini">
      <div className="nfx-card">
        <div className="nfx-filters" style={{ paddingTop: 18 }}>
          <select className="nfx-select"><option>Tutti gli stati</option></select>
          <select className="nfx-select"><option>Tutti i servizi</option></select>
          <label className="nfx-addr__radio" style={{ color: 'var(--nfx-muted)', fontSize: 13 }}>
            <input type="checkbox" /> Mostra soltanto gli ordini dei partner
          </label>
          <span className="nfx-tb__spacer" />
          <div className="nfx-search-field" style={{ maxWidth: 220 }}><input placeholder="CERCARE..." /><Search size={16} /></div>
        </div>
        {loading ? <div className="nfx-loading">Caricamento…</div> : ordini.length === 0 ? (
          <Empty icon={<Package size={40} />} title="Nessun ordine" />
        ) : (
          <table className="nfx-table">
            <thead><tr>
              <th>Numero</th><th>Data</th><th>Stato</th><th>Tipo</th><th>Partner</th><th className="num">Importo (tasse incluse)</th><th className="nfx-td-actions" />
            </tr></thead>
            <tbody>
              {ordini.map((o, i) => {
                const st = STATE_TAG(o.stato);
                return (
                  <tr key={o.id || i}>
                    <td className="nfx-cell-strong">{o.numero_ordine || '—'}</td>
                    <td>{fmtDate(o.created_at)}</td>
                    <td><span className={`nfx-st-text nfx-st-text--${st.cls === 'ok' ? 'ok' : st.cls === 'danger' ? 'danger' : 'ok'}`}>{(o.stato || '—').toUpperCase()}</span></td>
                    <td>{o.tipo === 'nuovo' || !o.tipo ? 'Ordine di nuovi prodotti' : cap(o.tipo)}</td>
                    <td>—</td>
                    <td className="num nfx-cell-strong">{EUR(o.importo_totale)}</td>
                    <td className="nfx-td-actions"><button className="nfx-iconbtn"><MoreVertical size={16} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </ContabPage>
  );
};

/* ═══════════════════ 4. DA PAGARE ═══════════════════ */
const prodIcon = (tipo) => {
  const t = String(tipo || '').toLowerCase();
  if (t.includes('vps')) return 'nav-vps-cloud';
  if (t.includes('hosting')) return 'nav-hosting-web';
  if (t.includes('dominio') || t.includes('domain')) return 'nav-domini';
  if (t.includes('pec')) return 'nav-pec';
  if (t.includes('ssl') || t.includes('certific')) return 'nav-ssl';
  if (t.includes('server') || t.includes('dedicat')) return 'nav-server-cloud';
  if (t.includes('nas') || t.includes('synology')) return 'nav-nas-synology';
  if (t.includes('storage') || t.includes('backup')) return 'nav-storage';
  return 'nav-cloud-computing';
};
const eurAmt = (v) => `${(parseFloat(v) || 0).toFixed(2)} EUR`;
const giorniAlla = (d) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
  return diff;
};
const scadClass = (g) => (g == null ? '' : g < 0 ? 'nfx-row--overdue' : g <= 14 ? 'nfx-row--soon' : '');
const Caret = () => <ArrowDownUp size={12} className="nfx-th-caret" />;

const DP_DASH = {
  vps: '/cloud-dedicated', cloud: '/cloud-dedicated', dedicato: '/cloud-dedicated',
  hosting: '/hosting-domains/hosting', hosting_web: '/hosting-domains/hosting',
  dominio: '/hosting-domains/domains', domini: '/hosting-domains/domains', domain: '/hosting-domains/domains',
  pec: '/pec-trust',
};
const PERIODI = [['I prossimi 7 giorni', 7], ['I prossimi 15 giorni', 15], ['Il prossimo mese', 30], ['I prossimi 2 mesi', 60], ['I prossimi 3 mesi', 90], ['Tutto', null]];

export const DaPagarePage = () => {
  const navigate = useNavigate();
  const [servizi, setServizi] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState(90);
  const [tipo, setTipo] = useState('');
  const [menu, setMenu] = useState(null);
  const [toast, setToast] = useState('');

  const load = React.useCallback(() => api.get('/api/fatturazione/servizi-rinnovo').then((r) => setServizi(r.data.servizi || [])).catch(() => setServizi([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const h = () => setMenu(null); document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, []);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 2600); return () => clearTimeout(t); }, [toast]);

  const all = servizi || [];
  const tipi = [...new Set(all.map((s) => s.tipo).filter(Boolean))];
  const rows = all.filter((s) => {
    if (q.trim() && !String(s.nome || '').toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (tipo && s.tipo !== tipo) return false;
    if (period != null) { const g = giorniAlla(s.data_scadenza); if (g == null || g > period) return false; }
    return true;
  });
  const allOn = rows.length > 0 && rows.every((s) => sel.has(s.id));
  const toggle = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(allOn ? new Set() : new Set(rows.map((s) => s.id)));
  const selected = all.filter((s) => sel.has(s.id));
  const totNet = selected.reduce((a, r) => a + (parseFloat(r.prezzo) || 0), 0);
  const iva = totNet * 0.22;

  const vedi = (s) => { setMenu(null); navigate(V2_BASE + (DP_DASH[String(s.tipo || '').toLowerCase()] || '/')); };
  const setAuto = (s) => {
    setMenu(null);
    const auto = s.rinnovo !== 'automatico';
    api.patch(`/api/rinnovi/${s.numero}`, { rinnovo_automatico: auto })
      .then(() => { setToast(auto ? 'Rinnovo automatico attivato' : 'Rinnovo automatico disattivato'); load(); })
      .catch(() => setToast('Operazione non riuscita'));
  };
  const proforma = () => {
    const items = selected.length ? selected : rows;
    if (!items.length) { setToast('Nessun servizio da includere'); return; }
    const net = items.reduce((a, r) => a + (parseFloat(r.prezzo) || 0), 0);
    const v = net * 0.22;
    const lines = items.map((r) => `- ${r.nome} (${r.durata || ''}): ${eurAmt(r.prezzo)}`).join('\n');
    const text = `FATTURA PRO FORMA — NeoFlux\n\nServizi:\n${lines}\n\nImponibile: ${eurAmt(net)}\nIVA 22%: ${eurAmt(v)}\nTotale: ${eurAmt(net + v)}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'pro-forma.txt'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const paga = () => {
    if (!selected.length) return;
    api.post('/api/rinnovi/paga', { numeri: selected.map((s) => s.numero) })
      .then(() => { setToast('Richiesta di pagamento inviata'); setSel(new Set()); load(); })
      .catch(() => setToast('Operazione non riuscita'));
  };

  return (
    <ContabPage crumb="Da pagare">
      <div className="nfx-card nfx-cb-card--pad">
        <div className="nfx-filters nfx-dp-filters">
          <div className="nfx-usel"><select value={period === null ? '' : period} onChange={(e) => setPeriod(e.target.value === '' ? null : Number(e.target.value))}>{PERIODI.map(([l, v]) => <option key={l} value={v === null ? '' : v}>{l}</option>)}</select></div>
          <div className="nfx-usel"><select value={tipo} onChange={(e) => setTipo(e.target.value)}><option value="">Tutti i tipi</option>{tipi.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <span className="nfx-tb__spacer" />
          <div className="nfx-dp-search"><input placeholder="CERCARE..." value={q} onChange={(e) => setQ(e.target.value)} /><Search size={16} /></div>
        </div>
        <table className="nfx-table nfx-dp-table">
          <thead><tr>
            <th className="nfx-th-check"><input type="checkbox" className="nfx-check" checked={allOn} onChange={toggleAll} /></th>
            <th>Servizi <Caret /></th>
            <th>Elementi <Caret /></th>
            <th>Etichetta <Caret /></th>
            <th>Scadenza <Caret /></th>
            <th className="num">Importo (Tasse escluse)</th>
            <th className="nfx-td-actions" />
          </tr></thead>
          {rows.length > 0 && (
            <tbody>
              {rows.map((s, i) => {
                const g = giorniAlla(s.data_scadenza);
                return (
                  <tr key={s.id || i} className={`${scadClass(g)}${sel.has(s.id) ? ' nfx-row--checked' : ''}`}>
                    <td className="nfx-td-check"><input type="checkbox" className="nfx-check" checked={sel.has(s.id)} onChange={() => toggle(s.id)} /></td>
                    <td><img className="nfx-dp-ic" src={`/nav/${prodIcon(s.tipo)}.svg`} alt="" aria-hidden="true" /></td>
                    <td className="nfx-cell-strong">{s.nome || '—'} {s.rinnovo === 'automatico' && <span className="nfx-dp-auto">auto</span>}</td>
                    <td>{s.etichetta ? <span className="nfx-tag-badge">{s.etichetta}</span> : ''}</td>
                    <td>{fmtDate(s.data_scadenza)} {g != null && <span className="nfx-dp-g">(G{g < 0 ? '+' : '-'}{Math.abs(g)})</span>}</td>
                    <td className="num nfx-cell-strong">{s.durata ? `${s.durata} - ${eurAmt(s.prezzo)}` : eurAmt(s.prezzo)}</td>
                    <td className="nfx-td-actions">
                      <div className="nfx-dp-menuwrap">
                        <button className="nfx-iconbtn" onClick={(e) => { e.stopPropagation(); setMenu(menu === s.id ? null : s.id); }}><MoreVertical size={16} /></button>
                        {menu === s.id && (
                          <div className="nfx-dp-menu" onClick={(e) => e.stopPropagation()}>
                            <button className="nfx-dp-menu__item" onClick={() => vedi(s)}><Package size={14} /> Vedere il prodotto</button>
                            <button className="nfx-dp-menu__item" onClick={() => setAuto(s)}><RefreshCw size={14} /> {s.rinnovo === 'automatico' ? 'Disattivare il rinnovo automatico' : 'Attivare il rinnovo automatico'}</button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
        {(servizi !== null && rows.length === 0) && (
          <Empty icon={<Search size={40} />} title="Nessun risultato trovato per questi criteri" />
        )}
        <div className="nfx-legend">
          <span className="nfx-legend__item"><span className="nfx-legend__sw" style={{ background: '#ffcdd2' }} /> Prodotti o fatture scaduti</span>
          <span className="nfx-legend__item"><span className="nfx-legend__sw" style={{ background: '#fff59d' }} /> Scade tra 14 giorni o meno</span>
          <span className="nfx-legend__item"><span className="nfx-legend__sw" style={{ background: '#FFECD9' }} /> Domini ripristinabili</span>
        </div>
        <div className="nfx-totals">
          <div className="nfx-totals__row"><span>Importo (Tasse escluse)</span><span>{eurAmt(totNet)}</span></div>
          <div className="nfx-totals__row"><span>IVA</span><span>{totNet ? eurAmt(iva) : '0.00 EUR'}</span></div>
          <div className="nfx-totals__row nfx-totals__row--grand"><span>Importo (Tasse incluse)</span><span>{eurAmt(totNet + iva)}</span></div>
        </div>
        <div className="nfx-cta-row">
          <button className="nfx-tb__btn nfx-tb__btn--ghost" onClick={proforma}>Generare una fattura pro forma</button>
          <button className="nfx-tb__btn" disabled={selected.length === 0} onClick={paga}>Pagare la mia selezione</button>
        </div>
      </div>
      {toast && <div className="nfx-dp-toast"><span>{toast}</span><button onClick={() => setToast('')}><X size={14} /></button></div>}
    </ContabPage>
  );
};

/* ═══════════════════ 5. RINNOVO ═══════════════════ */
export const RinnovoPage = () => {
  const [servizi, loading] = useFetch('/api/fatturazione/servizi-rinnovo', 'servizi', []);
  const navigate = useNavigate();
  const [selR, setSelR] = useState(() => new Set());
  const [qR, setQR] = useState('');
  const rows = servizi.filter((s) => !qR || String(s.nome || '').toLowerCase().includes(qR.trim().toLowerCase()));
  const allOnR = rows.length > 0 && rows.every((s) => selR.has(s.id));
  const toggleR = (id) => setSelR((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllR = () => setSelR(allOnR ? new Set() : new Set(rows.map((s) => s.id)));
  const intro = (
    <>
      <p className="nfx-cb-intro">
        Semplificati la vita e lascia che NeoFlux rinnovi i tuoi prodotti alla loro scadenza. Hai la certezza di non perdere mai i tuoi prodotti per errore. Potrai modificare le tue preferenze in qualsiasi momento. <a href="#info" onClick={e => e.preventDefault()}>Per più informazioni</a>
      </p>
      <p className="nfx-cb-intro">
        Renewal Warranty: assicurati di mantenere i tuoi prodotti, anche in caso di problemi! <a href="#info" onClick={e => e.preventDefault()}>Per più informazioni</a>
      </p>
    </>
  );
  return (
    <ContabPage crumb="Rinnovo" intro={intro}>
      <div className="nfx-card nfx-cb-card--pad">
        <div className="nfx-tb nfx-dp-filters">
          <button className="nfx-cfm-btn nfx-cfm-btn--default" disabled={selR.size === 0}>Gestire i rinnovi ▾</button>
          <button className="nfx-cfm-btn nfx-cfm-btn--default">Filtri ▾</button>
          <span className="nfx-tb__spacer" />
          <div className="nfx-dp-search"><input placeholder="CERCARE..." value={qR} onChange={(e) => setQR(e.target.value)} /><Search size={16} /></div>
        </div>
        <table className="nfx-table nfx-dp-table">
          <thead><tr>
            <th className="nfx-th-check"><input type="checkbox" className="nfx-check" checked={allOnR} onChange={toggleAllR} /></th>
            <th />
            <th>Elementi <Caret /></th>
            <th>Rinnovo automatico <Caret /></th>
            <th>Renewal Warranty <Caret /></th>
            <th>Scadenza <Caret /></th>
            <th className="nfx-td-actions" />
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><div className="nfx-loading">Caricamento…</div></td></tr>
            ) : rows.length ? rows.map((s, i) => {
              const auto = s.rinnovo === 'automatico';
              const g = giorniAlla(s.data_scadenza);
              return (
                <tr key={s.id || i}>
                  <td className="nfx-td-check"><input type="checkbox" className="nfx-check" checked={selR.has(s.id)} onChange={() => toggleR(s.id)} /></td>
                  <td><img className="nfx-dp-ic" src={`/nav/${prodIcon(s.tipo)}.svg`} alt="" aria-hidden="true" /></td>
                  <td className="nfx-cell-strong">{s.nome || '—'}</td>
                  <td>{auto ? <span className="nfx-txt-ok">Attivato</span> : <span className="nfx-badge-err">Disattivato</span>}</td>
                  <td className="nfx-dp-g">Disattivato</td>
                  <td>{fmtDate(s.data_scadenza)} {g != null && <span className="nfx-dp-g">(G{g < 0 ? '+' : '-'}{Math.abs(g)})</span>}</td>
                  <td className="nfx-td-actions"><button className="nfx-iconbtn"><MoreVertical size={16} /></button></td>
                </tr>
              );
            }) : (
              <tr><td colSpan={7}><div className="nfx-loading">Nessun risultato trovato per questi criteri.</div></td></tr>
            )}
          </tbody>
        </table>
        <div style={{ paddingTop: 20 }}>
          <button className="nfx-addfilter" onClick={() => navigate(V2_BASE + '/billing/metodi-pagamento')}>Gestire i miei profili di pagamento</button>
        </div>
      </div>
    </ContabPage>
  );
};

/* ═══════════════════ 6. METODI DI PAGAMENTO ═══════════════════ */
/* Marchio schema carta — disegni generici (nessun asset esterno) */
const CardBrand = ({ brand }) => {
  const b = (brand || '').toLowerCase();
  if (b.includes('master')) {
    return (
      <svg viewBox="0 0 48 30" width="46" height="29" aria-label="mastercard">
        <circle cx="19" cy="15" r="10" fill="#eb001b" />
        <circle cx="29" cy="15" r="10" fill="#f79e1b" />
        <path d="M24 7.6a10 10 0 0 0 0 14.8 10 10 0 0 0 0-14.8z" fill="#ff5f00" />
      </svg>
    );
  }
  if (b.includes('visa')) {
    return <span className="nfx-pmcard__wordmark nfx-pmcard__wordmark--visa">VISA</span>;
  }
  if (b.includes('amex') || b.includes('american')) {
    return <span className="nfx-pmcard__wordmark nfx-pmcard__wordmark--amex">AMEX</span>;
  }
  return <span className="nfx-pmcard__wordmark">{cap(brand) || 'CARTA'}</span>;
};

const CardChip = () => (
  <svg viewBox="0 0 32 24" width="30" height="22" aria-hidden="true" className="nfx-pmcard__chip">
    <rect x="1" y="1" width="30" height="22" rx="4" fill="#d9b25a" />
    <rect x="1" y="1" width="30" height="22" rx="4" fill="url(#chipg)" fillOpacity=".35" />
    <path d="M11 1v22M21 1v22M1 8h30M1 16h30" stroke="#b78e34" strokeWidth="1" />
    <rect x="11" y="8" width="10" height="8" rx="1.5" fill="#e7cd86" stroke="#b78e34" strokeWidth="1" />
    <defs>
      <linearGradient id="chipg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#fff" /><stop offset="1" stopColor="#c99e3c" />
      </linearGradient>
    </defs>
  </svg>
);

const brandClass = (brand) => {
  const b = (brand || '').toLowerCase();
  if (b.includes('master')) return 'nfx-pmcard--mastercard';
  if (b.includes('visa')) return 'nfx-pmcard--visa';
  if (b.includes('amex') || b.includes('american')) return 'nfx-pmcard--amex';
  return 'nfx-pmcard--default';
};

const PaymentCard = ({ m, onDelete, onSetDefault }) => (
  <article className="nfx-pmcard">
    <div className={`nfx-pmcard__top ${brandClass(m.provider)}`}>
      <div className="nfx-pmcard__brand"><CardBrand brand={m.provider} /></div>
      <div className="nfx-pmcard__mid">
        <CardChip />
        <div className="nfx-pmcard__num">
          <span>••••</span><span>••••</span><span>••••</span>
          <b>{m.ultime_cifre || '••••'}</b>
        </div>
      </div>
      <div className="nfx-pmcard__row">
        <span className="nfx-pmcard__holder">{m.intestatario || m.etichetta || '—'}</span>
        <span className="nfx-pmcard__exp">{m.scadenza || '—'}</span>
      </div>
    </div>
    <div className="nfx-pmcard__foot">
      <label className="nfx-pmcard__def">
        <button
          type="button"
          className={`nfx-radio${m.predefinito ? ' is-on' : ''}`}
          onClick={() => !m.predefinito && onSetDefault(m.id)}
          aria-pressed={!!m.predefinito}
          aria-label="Imposta come predefinito"
        />
        <span>Pagamento predefinito</span>
      </label>
      <button className="nfx-pmcard__del" onClick={() => onDelete(m)} aria-label="Elimina metodo">
        <Trash2 size={17} />
      </button>
    </div>
  </article>
);

/* Illustrazione empty-state (disegno originale, nessun asset esterno) */
const EmptyCardsIllu = () => (
  <svg className="nfx-mp-illu2" viewBox="0 0 220 150" width="220" height="150" fill="none" aria-hidden="true">
    <rect x="34" y="52" width="150" height="92" rx="12" fill="#eef1f8" transform="rotate(-8 109 98)" />
    <rect x="42" y="34" width="150" height="92" rx="12" fill="#fff" stroke="#d7dcea" strokeWidth="2" />
    <rect x="42" y="50" width="150" height="16" fill="#c9d2e8" />
    <rect x="56" y="86" width="46" height="10" rx="3" fill="#e2e7f2" />
    <rect x="56" y="102" width="80" height="8" rx="3" fill="#eef1f8" />
    <circle cx="168" cy="112" r="20" fill="#1d3c51" />
    <path d="M168 104v16M160 112h16" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

export const MetodiPage = () => {
  const [list, setList] = useState([]);
  const [loadingM, setLoadingM] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [ricaricaOpen, setRicaricaOpen] = useState(false);
  const [conto, , reloadConto] = useFetch('/api/conto-prepagato', null, { saldo: 0, movimenti: [] });
  const movimenti = conto?.movimenti || [];

  const load = React.useCallback(() => {
    setLoadingM(true);
    api.get('/api/metodi-pagamento')
      .then(r => setList(Array.isArray(r.data) ? r.data : []))
      .catch(() => setList([]))
      .finally(() => setLoadingM(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = (m) => {
    if (!window.confirm(`Eliminare la carta •••• ${m.ultime_cifre || ''}?`)) return;
    api.delete(`/api/metodi-pagamento/${m.id}`).then(load).catch(() => {});
  };
  const handleSetDefault = (id) => {
    api.put(`/api/metodi-pagamento/${id}/predefinito`).then(load).catch(() => {});
  };

  const isEmpty = !loadingM && list.length === 0;

  return (
    <ContabPage crumb="Metodi di pagamento" title="Metodi di pagamento">
      {loadingM ? (
        <div className="nfx-card nfx-card--pad"><div className="nfx-loading">Caricamento…</div></div>
      ) : isEmpty ? (
        /* ── Empty state: colonna a sinistra + illustrazione ── */
        <div className="nfx-mp-empty2">
          <div className="nfx-mp-empty2__col">
            <span className="nfx-mp-empty2__title">Nessuna modalità di pagamento registrata</span>
            <span className="nfx-mp-empty2__desc">Gestisci i metodi di pagamento che utilizzi per rinnovare i tuoi prodotti e pagare le fatture.</span>
            <button className="nfx-btn-blue nfx-mp-empty2__btn" onClick={() => setAddOpen(true)}>
              <Plus size={16} /> Aggiungi una modalità di pagamento
            </button>
          </div>
          <EmptyCardsIllu />
        </div>
      ) : (
        /* ── Lista carte ── */
        <div className="nfx-card nfx-card--pad">
          <div className="nfx-mp-head">
            <div>
              <h2 className="nfx-mp-head__title">Metodi di pagamento registrati</h2>
              <p className="nfx-mp-head__sub">Gestisci i metodi di pagamento che utilizzi per rinnovare i tuoi prodotti e pagare le fatture.</p>
            </div>
            <button className="nfx-btn-blue nfx-btn-blue--sec" onClick={() => setAddOpen(true)}>
              <Plus size={16} /> Aggiungi una modalità di pagamento
            </button>
          </div>
          <div className="nfx-pmgrid">
            {list.map((m, i) => (
              <PaymentCard key={m.id || i} m={m} onDelete={handleDelete} onSetDefault={handleSetDefault} />
            ))}
          </div>
        </div>
      )}

      <div className="nfx-card nfx-card--pad">
        <div className="nfx-mp-head" style={{ marginBottom: 20 }}>
          <div>
            <h2 className="nfx-mp-head__title">Conto prepagato</h2>
            <p className="nfx-mp-head__sub">Ricarica il tuo conto prepagato</p>
          </div>
        </div>
        <div className="nfx-prepaid nfx-prepaid--row">
          <div className="nfx-prepaid__left">
            <span className="nfx-prepaid__label">Saldo del conto :</span>
            <div className="nfx-prepaid__amount">{EUR(conto?.saldo)}</div>
          </div>
          <button className="nfx-btn-blue" onClick={() => setRicaricaOpen(true)}><Plus size={16} /> Carica credito sul conto</button>
        </div>

        <div className="nfx-sec__title" style={{ margin: '28px 0 12px' }}>Cronologia delle operazioni</div>
        {movimenti.length === 0 ? (
          <Empty icon={<FileText size={40} />} title="Cronologia delle operazioni vuota" sub="Al momento non hai registrato nessuna operazione" />
        ) : (
          <table className="nfx-table">
            <thead><tr><th>Data</th><th>Descrizione</th><th className="num">Importo</th></tr></thead>
            <tbody>{movimenti.map((mv, i) => (
              <tr key={i}><td>{fmtDate(mv.creato_il || mv.data)}</td><td>{mv.descrizione || '—'}</td><td className="num">{EUR(mv.importo)}</td></tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {addOpen && (
        <AddCardModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}

      {ricaricaOpen && (
        <RicaricaModal
          metodi={list}
          onAddCard={() => { setRicaricaOpen(false); setAddOpen(true); }}
          onClose={() => setRicaricaOpen(false)}
          onDone={() => { setRicaricaOpen(false); reloadConto(); }}
        />
      )}
    </ContabPage>
  );
};

/* Modale ricarica conto prepagato */
const RICARICA_PRESET = [10, 25, 50, 100, 250];
const RicaricaModal = ({ metodi, onAddCard, onClose, onDone }) => {
  const [importo, setImporto] = useState(25);
  const [metodoId, setMetodoId] = useState((metodi[0] && metodi[0].id) || '');
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState('');
  const [ok, setOk] = useState(false);
  const noMetodi = !metodi || metodi.length === 0;

  const submit = () => {
    const val = Number(importo);
    if (!(val > 0)) { setErrore('Inserisci un importo valido.'); return; }
    setSaving(true); setErrore('');
    api.post('/api/conto-prepagato/ricarica', { importo: val, metodo_id: metodoId })
      .then(() => setOk(true))
      .catch(e => setErrore(e?.response?.data?.messaggio || 'Impossibile registrare la ricarica.'))
      .finally(() => setSaving(false));
  };

  return (
    <div className="nfx-modal-scrim" onClick={onClose}>
      <div className="nfx-modal" onClick={e => e.stopPropagation()}>
        <div className="nfx-modal__head">
          <span className="nfx-modal__title">Carica credito sul conto</span>
          <button className="nfx-modal__close" onClick={onClose} aria-label="Chiudi"><X size={20} /></button>
        </div>

        {ok ? (
          <>
            <div className="nfx-modal__body">
              <div className="nfx-mp-empty2__title" style={{ marginBottom: 8 }}>Richiesta di ricarica registrata</div>
              <p className="nfx-mp-head__sub">La ricarica di {EUR(importo)} è stata registrata e verrà elaborata a breve. Il saldo si aggiornerà a conferma dell&rsquo;addebito.</p>
            </div>
            <div className="nfx-modal__foot">
              <button className="nfx-btn-pri" onClick={onDone}>Chiudi</button>
            </div>
          </>
        ) : noMetodi ? (
          <>
            <div className="nfx-modal__body">
              <p className="nfx-mp-head__sub">Per ricaricare il conto prepagato devi prima registrare una modalità di pagamento.</p>
            </div>
            <div className="nfx-modal__foot">
              <button className="nfx-btn-sec" onClick={onClose}>Annulla</button>
              <button className="nfx-btn-pri" onClick={onAddCard}><Plus size={16} /> Aggiungi una modalità di pagamento</button>
            </div>
          </>
        ) : (
          <>
            <div className="nfx-modal__body">
              <div className="nfx-cardfld__lbl">Importo della ricarica</div>
              <div className="nfx-ric-presets">
                {RICARICA_PRESET.map(v => (
                  <button key={v} type="button"
                    className={'nfx-ric-chip' + (Number(importo) === v ? ' is-on' : '')}
                    onClick={() => setImporto(v)}>{EUR(v)}</button>
                ))}
              </div>
              <label className="nfx-cardfld" style={{ marginTop: 16 }}>
                <span className="nfx-cardfld__lbl">Importo personalizzato (€)</span>
                <input className="nfx-cardfld__in" type="number" min="1" step="1"
                  value={importo} onChange={e => setImporto(e.target.value)} />
              </label>
              <label className="nfx-cardfld">
                <span className="nfx-cardfld__lbl">Metodo di pagamento</span>
                <select className="nfx-cardfld__in" value={metodoId} onChange={e => setMetodoId(e.target.value)}>
                  {metodi.map(m => (
                    <option key={m.id} value={m.id}>
                      {(m.marca || m.brand || 'Carta')} •••• {m.ultime_cifre || m.last4 || '····'}
                    </option>
                  ))}
                </select>
              </label>
              {errore && <div className="nfx-carderr">{errore}</div>}
            </div>
            <div className="nfx-modal__foot">
              <button className="nfx-btn-sec" onClick={onClose} disabled={saving}>Annulla</button>
              <button className="nfx-btn-pri" onClick={submit} disabled={saving}>
                {saving ? 'Elaborazione…' : `Ricarica ${EUR(importo)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════ 7. IMPOSTAZIONI DI FATTURAZIONE ═══════════════════ */
export const ImpostazioniPage = () => {
  const [profilo] = useFetch('/api/profilo', null, null);
  const [emitPdf, setEmitPdf] = useState(false);
  const [notif, setNotif] = useState({ nuova_fattura: true, scadenza: false, addebito: false });
  const addr = profilo?.indirizzo || {};
  const nome = profilo?.ragione_sociale || `${profilo?.nome || ''} ${profilo?.cognome || ''}`.trim() || '—';
  const via = addr.via || addr.indirizzo || profilo?.indirizzo_fatturazione?.via || '';
  const citta = [addr.cap || profilo?.cap, addr.citta || profilo?.citta].filter(Boolean).join(' ');
  const paese = (addr.paese || 'ITALIA');

  const AF0 = { tipo: 'privato', ragione: '', nome: '', cognome: '', cf: '', piva: '', pivaUe: '', sdi: '', pec: '', prefisso: '+39', telefono: '', indirizzo: '', complemento: '', cap: '', citta: '', provincia: '', nazione: 'IT' };
  const [addrModal, setAddrModal] = useState(null); // null | 'add' | 'edit'
  const [af, setAf] = useState(AF0);
  const openAdd = () => { setAf(AF0); setAddrModal('add'); };
  const openEdit = () => {
    const ind = Array.isArray(profilo?.indirizzi) ? profilo.indirizzi : [];
    const a0 = ind.find((x) => x?.tipo === 'fatturazione') || ind.find((x) => x?.tipo === 'principale') || ind[0] || {};
    setAf({
      ...AF0,
      tipo: profilo?.tipo_cliente || 'privato',
      ragione: profilo?.ragione_sociale || '', nome: profilo?.nome || '', cognome: profilo?.cognome || '',
      cf: profilo?.codice_fiscale || profilo?.codice_fiscale_org || '', piva: profilo?.partita_iva || '', pivaUe: profilo?.partita_iva_comunitaria || '', sdi: profilo?.codice_sdi || '', pec: profilo?.pec || '',
      prefisso: profilo?.prefisso_telefono || '+39', telefono: profilo?.telefono || '',
      indirizzo: a0.indirizzo || '', cap: a0.cap || '', citta: a0.citta || '', provincia: a0.provincia || '', nazione: a0.nazione || 'IT',
    });
    setAddrModal('edit');
  };
  const chAf = (k) => (e) => setAf((s) => ({ ...s, [k]: e.target.value }));
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const paeseName = (code) => (PAESI.find((p) => p.code === code)?.name) || code || '';
  const saveAddr = async () => {
    setSaving(true); setSaveErr('');
    try {
      await api.put('/api/fatturazione/indirizzo', af);
      setSaved({ ...af });
      setAddrModal(null);
    } catch (e) {
      setSaveErr(e.response?.data?.messaggio || 'Salvataggio non riuscito.');
    } finally { setSaving(false); }
  };
  const fx = fiscalFor(af.nazione, af.tipo);

  return (
    <ContabPage crumb="Impostazioni di fatturazione" title="Impostazioni di fatturazione">
      {/* Indirizzi */}
      <div className="nfx-card nfx-card--pad">
        <div className="nfx-sec__head">
          <div className="nfx-sec__title">Indirizzi di fatturazione</div>
          <button className="nfx-btn-blue" onClick={openAdd}><Plus size={16} /> Aggiungere un indirizzo di fatturazione</button>
        </div>
        <div className="nfx-sec__desc">Aggiungi e gestisci i tuoi indirizzi di fatturazione per semplificare i tuoi ordini.</div>
        <div className="nfx-addr">
          <div className="nfx-addr__body">
            <div className="nfx-addr__name">{saved ? (saved.tipo !== 'privato' ? (saved.ragione || `${saved.nome} ${saved.cognome}`.trim()) : `${saved.nome} ${saved.cognome}`.trim() || '—') : nome}</div>
            <div className="nfx-addr__line">{(saved ? saved.indirizzo : via) || '—'}</div>
            <div className="nfx-addr__line">{saved ? [[saved.cap, saved.citta].filter(Boolean).join(' '), paeseName(saved.nazione)].filter(Boolean).join(', ') : [citta, paese].filter(Boolean).join(', ')}</div>
          </div>
          <div className="nfx-addr__foot">
            <label className="nfx-addr__radio"><input type="radio" defaultChecked /> Indirizzo predefinito</label>
            <button className="nfx-addr__edit" onClick={openEdit}>Modificare</button>
            <button className="nfx-addr__del"><Trash2 size={16} /></button>
          </div>
        </div>
      </div>

      {/* Emissione fatture */}
      <div className="nfx-card nfx-card--pad">
        <div className="nfx-sec__title" style={{ marginBottom: 16 }}>Emissione delle fatture</div>
        <div className="nfx-setting">
          <span className="nfx-setting__ic"><FileText size={18} /></span>
          <div className="nfx-setting__body">
            <div className="nfx-setting__name">Includi le fatture in PDF nelle e-mail</div>
            <div className="nfx-setting__desc">Ricevi le fatture in formato PDF come allegati alle tue e-mail legate alla fatturazione.</div>
          </div>
          <Toggle on={emitPdf} onClick={() => setEmitPdf(v => !v)} />
        </div>
        <div className="nfx-setting">
          <span className="nfx-setting__ic"><Pencil size={18} /></span>
          <div className="nfx-setting__body">
            <div className="nfx-setting__name">Riferimento della fattura</div>
            <div className="nfx-setting__desc">Questo riferimento comparirà su tutte le fatture che ti emetteremo.</div>
          </div>
          <button className="nfx-setting__link"><Plus size={15} /> Aggiungere un riferimento</button>
        </div>
      </div>

      {/* Notifiche */}
      <div className="nfx-card nfx-card--pad">
        <div className="nfx-sec__head">
          <div className="nfx-sec__title">Notifiche</div>
          <Toggle on={notif.nuova_fattura && notif.scadenza && notif.addebito}
            onClick={() => { const all = !(notif.nuova_fattura && notif.scadenza && notif.addebito); setNotif({ nuova_fattura: all, scadenza: all, addebito: all }); }} />
        </div>
        <div className="nfx-sec__desc"><a style={{ color: 'var(--nfx-blue)' }} href="#u" onClick={e => e.preventDefault()}>1 utente</a> della tua organizzazione riceverà le seguenti notifiche:</div>
        <div className="nfx-setting">
          <span className="nfx-setting__ic"><Receipt size={18} /></span>
          <div className="nfx-setting__body">
            <div className="nfx-setting__name">Nuova fattura</div>
            <div className="nfx-setting__desc">Ricevi una e-mail quando viene emessa una nuova fattura.</div>
          </div>
          <Toggle on={notif.nuova_fattura} onClick={() => setNotif(n => ({ ...n, nuova_fattura: !n.nuova_fattura }))} />
        </div>
        <div className="nfx-setting">
          <span className="nfx-setting__ic"><Calendar size={18} /></span>
          <div className="nfx-setting__body">
            <div className="nfx-setting__name">Data di scadenza</div>
            <div className="nfx-setting__desc">Ricevi una e-mail promemoria prima della data di scadenza del rinnovo automatico di un prodotto (14 giorni prima della scadenza).</div>
          </div>
          <Toggle on={notif.scadenza} onClick={() => setNotif(n => ({ ...n, scadenza: !n.scadenza }))} />
        </div>
        <div className="nfx-setting">
          <span className="nfx-setting__ic"><ArrowDownUp size={18} /></span>
          <div className="nfx-setting__body">
            <div className="nfx-setting__name">Addebito automatico</div>
            <div className="nfx-setting__desc">Ricevi una e-mail di conferma dell'addebito automatico.</div>
          </div>
          <Toggle on={notif.addebito} onClick={() => setNotif(n => ({ ...n, addebito: !n.addebito }))} />
        </div>
      </div>

      {addrModal && (
        <div className="nfx-dlg-scrim" onClick={() => setAddrModal(null)}>
          <div className="nfx-dlg" onClick={(e) => e.stopPropagation()}>
            <div className="nfx-dlg__head">
              <h3>{addrModal === 'edit' ? 'Modificare l’indirizzo di fatturazione' : 'Aggiungere un indirizzo di fatturazione'}</h3>
              <button className="nfx-dlg__x" onClick={() => setAddrModal(null)} aria-label="Chiudi"><X size={16} /></button>
            </div>
            <form id="nfx-addr-form" className="nfx-dlg__body" onSubmit={(e) => { e.preventDefault(); saveAddr(); }}>
              {saveErr && <div className="nfx-carderr">{saveErr}</div>}
              <div className="nfx-dlg__field"><label>Tipo di cliente</label>
                <select value={af.tipo} onChange={chAf('tipo')}>
                  {TIPO_CLIENTE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {fx.fields.ragioneSociale && (
                <div className="nfx-dlg__field"><label>Ragione sociale</label><input value={af.ragione} onChange={chAf('ragione')} /></div>
              )}
              <div className="nfx-dlg__row">
                <div className="nfx-dlg__field"><label>Nome</label><input value={af.nome} onChange={chAf('nome')} /></div>
                <div className="nfx-dlg__field"><label>Cognome</label><input value={af.cognome} onChange={chAf('cognome')} /></div>
              </div>
              <div className="nfx-dlg__row">
                <div className="nfx-dlg__field" style={{ flex: '0 0 110px' }}><label>Prefisso</label><input value={af.prefisso} onChange={chAf('prefisso')} /></div>
                <div className="nfx-dlg__field"><label>Telefono</label><input value={af.telefono} onChange={chAf('telefono')} /></div>
              </div>
              <div className="nfx-dlg__field"><label>Indirizzo</label><input value={af.indirizzo} onChange={chAf('indirizzo')} /></div>
              <div className="nfx-dlg__field"><label>Complemento (facoltativo)</label><input value={af.complemento} onChange={chAf('complemento')} /></div>
              <div className="nfx-dlg__row">
                <div className="nfx-dlg__field" style={{ flex: '0 0 120px' }}><label>CAP</label><input value={af.cap} onChange={chAf('cap')} /></div>
                <div className="nfx-dlg__field"><label>Città</label><input value={af.citta} onChange={chAf('citta')} /></div>
                <div className="nfx-dlg__field" style={{ flex: '0 0 100px' }}><label>Provincia</label><input value={af.provincia} onChange={chAf('provincia')} maxLength={2} /></div>
              </div>
              <div className="nfx-dlg__field"><label>Nazione</label>
                <select value={af.nazione} onChange={chAf('nazione')}>
                  {PAESI.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                </select>
              </div>
              {(fx.fields.codiceFiscale || fx.fields.codiceFiscaleOrg) && (
                <div className="nfx-dlg__field"><label>{fx.fields.codiceFiscaleOrg ? 'Codice Fiscale (ente/azienda)' : 'Codice Fiscale'}{fx.fields.codiceFiscaleReq ? ' *' : ''}</label><input value={af.cf} onChange={chAf('cf')} maxLength={fx.fields.codiceFiscaleOrg ? 11 : 16} /></div>
              )}
              {fx.fields.partitaIva && (
                <div className="nfx-dlg__field"><label>Partita IVA</label><input value={af.piva} onChange={chAf('piva')} maxLength={11} /></div>
              )}
              {fx.fields.partitaIvaComunitaria && (
                <div className="nfx-dlg__field"><label>Partita IVA comunitaria (UE — VIES)</label><input value={af.pivaUe} onChange={chAf('pivaUe')} placeholder={`${af.nazione}…`} /></div>
              )}
              {fx.fields.taxId && (
                <div className="nfx-dlg__field"><label>Codice fiscale / Tax ID (estero)</label><input value={af.piva} onChange={chAf('piva')} /></div>
              )}
              {fx.fields.sdiPec && (
                <div className="nfx-dlg__row">
                  <div className="nfx-dlg__field"><label>Codice destinatario (SDI)</label><input value={af.sdi} onChange={chAf('sdi')} maxLength={fx.fields.sdiLen} /></div>
                  <div className="nfx-dlg__field"><label>PEC</label><input value={af.pec} onChange={chAf('pec')} /></div>
                </div>
              )}
              <div className="nfx-dlg__nota">{fx.nota}</div>
            </form>
            <div className="nfx-dlg__foot">
              <button className="nfx-cfm-btn nfx-cfm-btn--default" disabled={saving} onClick={() => setAddrModal(null)}>Annullare</button>
              <button className="nfx-cfm-btn nfx-cfm-btn--primary" type="submit" form="nfx-addr-form" disabled={saving}>{saving ? 'Attendere…' : 'Convalidare'}</button>
            </div>
          </div>
        </div>
      )}
    </ContabPage>
  );
};
